import { useCommentsTab } from '../hooks';
import { Box, Button, TextField, Typography } from '@mui/material';
import { t } from 'i18next';
import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import { MAXIMUM_COMMENT_LENGTH } from '@common/types/constants.ts';
import { FC } from 'react';
import { components } from '@types';
import Comment from './Comment.tsx';
import { SaveChangesModal, StatusBanner } from '@components';

interface CommentsProps {
  task_id: string;
  commentsList: components['schemas']['TaskCommentDTO'][];
  isFormClean: boolean;
  setIsFormClean: (clean: boolean) => void;
  triggerTabChange: boolean;
  setTriggerTabChange: (trigger: boolean) => void;
  triggerCloseDrawer: boolean;
  setTriggerCloseDrawer: (trigger: boolean) => void;
  tabIndex: string;
  setNewTabIndex: (newTabIndex: string) => void;
  closeDrawer: () => void;
  isFormDisabled: boolean;
  lockedBy: {
    name: string;
    role: string;
  };
  handleUnlockLockedTask: () => void;
  isUnlockInProgress: boolean;
  isUnlockDisabled: boolean;
}

export const CommentsTab: FC<CommentsProps> = ({
  commentsList,
  task_id,
  setIsFormClean,
  tabIndex,
  setNewTabIndex,
  triggerTabChange,
  setTriggerTabChange,
  isFormClean,
  triggerCloseDrawer,
  setTriggerCloseDrawer,
  closeDrawer,
  isFormDisabled,
  isUnlockInProgress,
  isUnlockDisabled,
  lockedBy,
  handleUnlockLockedTask,
}) => {
  const {
    formMethods,
    onSubmit,
    isOpen,
    handleOpenCreateComment,
    handleCloseCreateComment,
    isValid,
    isSubmitting,
    user_id,
    commentToEditId,
    handleSetCommentToEdit,
    handleRemoveCommentToEdit,
    newCommentToEditId,
    setNewCommentToEditId,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    triggerOpenCreateComment,
    setTriggerOpenCreateComment,
    inputRef,
    forceOpenCreateComment,
    canAddComment,
  } = useCommentsTab({
    task_id,
    setIsFormClean,
    tabIndex,
    setNewTabIndex,
    triggerTabChange,
    setTriggerTabChange,
    isFormClean,
    triggerCloseDrawer,
    setTriggerCloseDrawer,
    closeDrawer,
  });

  const commentsListHeight = (isOpen ? 570 : 430) + (isFormDisabled ? 65 : 0);

  const { control } = formMethods;
  return (
    <Box>
      {isFormDisabled && lockedBy.name && (
        <StatusBanner
          text={t('common.lockedByBanner.message', {
            name: lockedBy.name,
            role: t(`roles.${lockedBy.role}`),
          })}
          variant={'warning'}
          action={handleUnlockLockedTask}
          actionText={t('common.lockedByBanner.unlock')}
          actionLoading={isUnlockInProgress}
          actionDisabled={isUnlockDisabled}
        />
      )}
      <Typography
        variant='subtitle2'
        sx={{
          mb: 0.5,
        }}
      >
        {t('worklist.worklistItem.comments.addNewComment')}
      </Typography>

      <FormProvider {...formMethods}>
        <Form
          control={control}
          onSubmit={e => onSubmit(e.event)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: 'fit-content',
            position: 'relative',
            gap: '1rem',
            marginTop: 8,
          }}
        >
          <Controller
            name='comment'
            control={control}
            disabled={isSubmitting || !canAddComment}
            render={({ field, fieldState: { error } }) => (
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                }}
              >
                {!isOpen && (
                  <Button
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      height: '100%',
                      width: '100%',
                      zIndex: 5,
                    }}
                    onClick={e => {
                      if (commentToEditId || isFormDisabled) {
                        return;
                      }
                      e.preventDefault();
                      e.stopPropagation();
                      handleOpenCreateComment();
                    }}
                    disabled={!canAddComment}
                  />
                )}
                <TextField
                  {...field}
                  sx={{ bgcolor: 'white' }}
                  type='text'
                  size='medium'
                  label={t('worklist.worklistItem.comments.comment')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={
                    error
                      ? error.message
                      : `${field.value ? field.value.length + `/${MAXIMUM_COMMENT_LENGTH}` : ''}`
                  }
                  fullWidth
                  multiline
                  disabled={
                    !!commentToEditId ||
                    isFormDisabled ||
                    !canAddComment ||
                    isSubmitting
                  }
                  minRows={isOpen ? 4 : 1}
                  maxRows={4}
                  onBlur={() => {
                    if (!field.value && isOpen) {
                      handleCloseCreateComment();
                    }
                  }}
                  inputRef={inputRef}
                  slotProps={{
                    formHelperText: {
                      style: {
                        textAlign: error ? 'left' : 'right',
                        margin: 0,
                        padding: '3px 14px 0',
                      },
                    },
                  }}
                />
                {isOpen && (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: '8px',
                      mt: 1,
                    }}
                  >
                    <Button
                      variant='text'
                      sx={{
                        fontSize: '0,875rem',
                        fontWeight: 'medium',
                        lineHeight: '24px',
                      }}
                      disabled={isSubmitting}
                      onClick={handleCloseCreateComment}
                    >
                      {t('general.cancel')}
                    </Button>
                    <Button
                      type='submit'
                      variant='contained'
                      color='secondary'
                      sx={{
                        fontSize: '0,875rem',
                        fontWeight: 'medium',
                        lineHeight: '24px',
                        color: 'white',
                      }}
                      disabled={!isValid || !field.value || isSubmitting}
                    >
                      {t('general.save')}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          />
          <SaveChangesModal
            isOpen={isSaveChangesModalOpen}
            onClose={handleCloseSaveChangesModal}
            isValid={isValid}
            handleSaveChanges={handleSaveChanges}
            discardChanges={handleDiscardChanges}
            isLoading={isSubmitting}
          />
        </Form>
      </FormProvider>

      <Typography
        variant='subtitle2'
        sx={{
          mt: 3,
          mb: 0.5,
        }}
      >
        {t('worklist.worklistItem.comments.allComments')}
      </Typography>

      {commentsList.length === 0 ? (
        <Typography variant='body2' sx={{ color: 'text.disabled' }}>
          {t('worklist.worklistItem.comments.noComments')}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            overflow: 'auto',
            maxHeight: `calc(100vh - ${commentsListHeight}px)`,
          }}
        >
          {commentsList.map(comment => {
            return (
              <Comment
                details={comment}
                key={comment.id}
                isCommentOwner={comment.created_by.id === user_id}
                task_id={task_id}
                commentToEditId={commentToEditId}
                handleSetCommentToEdit={handleSetCommentToEdit}
                handleRemoveCommentToEdit={handleRemoveCommentToEdit}
                setIsFormClean={setIsFormClean}
                newCommentToEditId={newCommentToEditId}
                setNewCommentToEditId={setNewCommentToEditId}
                isFormClean={isFormClean}
                triggerTabChange={triggerTabChange}
                setTriggerTabChange={setTriggerTabChange}
                handleOpenCreateComment={forceOpenCreateComment}
                triggerOpenCreateComment={triggerOpenCreateComment}
                setTriggerOpenCreateComment={setTriggerOpenCreateComment}
                closeDrawer={closeDrawer}
                triggerCloseDrawer={triggerCloseDrawer}
                setTriggerCloseDrawer={setTriggerCloseDrawer}
                isFormDisabled={isFormDisabled}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};
